/**
 * YOUR OWN PAGE — who you are, what you have done, what you hold.
 *
 * Anthony: "Make Kent a page and send him an email to fill out experience
 * and certifications etc."
 *
 * Until now the only record of a person in here was a login and a role.
 * Which is enough to sign in and not enough to answer the one question a
 * service advisor asks every morning: who should take this job. "Eleven
 * years, mostly German, ex-dealer, does not do transmissions" answers it.
 * A tick-box skills matrix never does, which is why every field here is
 * free text.
 *
 * YOU FILL YOUR OWN IN. That is the point — it is your page, not a form an
 * admin completes about you, and the database agrees: the policies added
 * in 0013 let a person write their own row and nobody else's. An admin can
 * open anybody's and edit it, because somebody has to be able to fix a
 * typo for a technician who has never signed in.
 *
 * CERTIFICATIONS LIVE HERE TOO, and can now belong to a profile rather
 * than a technician record — Kent is an advisor and has no wrench record,
 * and inventing one to hold a certificate would have put him in every
 * "who is doing this job" dropdown in the system.
 */

import { db } from "../data/client.ts";
import { setMyPassword } from "../data/session.ts";
import { esc, mount } from "../ui/dom.ts";
import { TRACKS, CERT_BODY, trackFor, studyUrl, examUrl } from "../domain/cert-tracks.ts";
import type { Role } from "../domain/permissions.ts";
import { motionOn, setMotion } from "../ui/motion.ts";

const BOSS: Role[] = ["admin", "supervisor"];

type Sp = {
  profile_id: string; headline: string | null; years: number | null;
  worked_at: string | null; strengths: string | null; avoid: string | null;
  bio: string | null;
};

type Cert = {
  id: string; body: string; name: string; reference: string | null;
  earned_on: string | null; expires_on: string | null;
  technician_id: string | null; profile_id: string | null;
};

const val = (id: string): string =>
  (document.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | null)
    ?.value.trim() ?? "";

/**
 * `who` is a profile id. Blank means "me", which is the only one most
 * people will ever open.
 */
export async function mePage(role: Role = "viewer", who?: string): Promise<void> {
  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>My page</h1></header>
    <div id="mepage"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  const host = document.getElementById("mepage");
  if (!host) return;

  try {
    const sb = await db();
    const { data: auth } = await sb.auth.getUser();
    const meId = auth.user?.id ?? "";
    const id = who || meId;
    const mine = id === meId;
    const canEdit = (mine || BOSS.includes(role)) && role !== "viewer";

    const [profRes, spRes, certRes, techRes] = await Promise.all([
      sb.from("profiles").select("id,account_id,full_name,email,phone,role,active")
        .eq("id", id).maybeSingle(),
      sb.from("staff_profiles")
        .select("profile_id,headline,years,worked_at,strengths,avoid,bio")
        .eq("profile_id", id).maybeSingle(),
      // Certificates owned either way: the person, or the wrench record
      // they are linked to. Somebody with both sees both.
      sb.from("certifications")
        .select("id,body,name,reference,earned_on,expires_on,technician_id,profile_id")
        .order("earned_on", { ascending: false, nullsFirst: false }).limit(200),
      sb.from("technicians").select("id,profile_id").eq("profile_id", id),
    ]);

    const prof = profRes.data as {
      id: string; account_id: string; full_name: string;
      email: string | null; phone: string | null; role: string; active: boolean;
    } | null;
    if (!prof) { host.innerHTML = `<div class="empty">No such person.</div>`; return; }

    const sp = (spRes.data ?? null) as Sp | null;
    const techIds = ((techRes.data ?? []) as Array<{ id: string }>).map((t) => t.id);
    const certs = ((certRes.data ?? []) as unknown as Cert[]).filter((c) =>
      c.profile_id === id || (c.technician_id !== null && techIds.includes(c.technician_id)));

    const line = (label: string, v: string | null) => v
      ? `<div class="melabel">${esc(label)}</div><div class="meval">${esc(v)}</div>` : "";

    host.innerHTML = `
      <div class="card">
        <div class="mehead">
          <div class="meav">${esc(prof.full_name.trim().split(/\s+/).slice(0, 2)
            .map((w) => w[0] ?? "").join("").toUpperCase())}</div>
          <div>
            <h2>${esc(prof.full_name)}</h2>
            <p class="quiet">${esc(sp?.headline || prof.role)}${
              sp?.years ? ` &middot; ${sp.years} years in the trade` : ""}</p>
            <p class="quiet small">${esc(prof.email ?? "no email on file")}${
              prof.phone ? ` &middot; ${esc(prof.phone)}` : ""}</p>
          </div>
        </div>
        ${(sp?.worked_at || sp?.strengths || sp?.avoid || sp?.bio) ? `
          <div class="megrid">
            ${line("Where they have worked", sp?.worked_at ?? null)}
            ${line("Send them", sp?.strengths ?? null)}
            ${line("Do not send them", sp?.avoid ?? null)}
            ${line("About", sp?.bio ?? null)}
          </div>` : `<div class="pad"><p class="quiet">${
            mine
              ? "Nothing filled in yet. Tell the shop what you are good at &mdash; it decides which jobs come your way."
              : "This page has not been filled in yet."}</p></div>`}
        ${canEdit ? `<div class="pad"><button class="btn primary" id="me-edit">${
          sp ? "Edit my details" : "Fill it in"}</button></div>` : ""}
        ${mine ? `<div class="pad"><button class="btn" id="me-pw">Set a password</button></div>` : ""}
      </div>

      ${mine ? `<div class="card">
        <div class="card-head">My settings</div>
        <label class="row mepref">
          <div class="row-main">
            <div class="row-title">Animations</div>
            <div class="row-sub">The turning coin, the light round the big buttons, sliding panels.
              Turn it off and everything holds still, on every device you sign in on.</div>
          </div>
          <div class="row-side"><input type="checkbox" id="me-motion"${motionOn() ? " checked" : ""}></div>
        </label>
      </div>` : ""}

      <div class="card">
        <div class="card-head">Certifications &middot; ${certs.length}</div>
        ${certs.length ? certs.map((c) => {
          const t = trackFor(c.body, c.name);
          return `<div class="row">
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(c.body)} ${esc(c.name)}</span>
                ${t ? `<span class="pill">${esc(t.title)}</span>` : ""}</div>
              <div class="row-sub">${c.earned_on
                ? `earned ${esc(c.earned_on)}` : "no date recorded"}${
                c.expires_on ? `<span class="dot">&middot;</span>expires ${esc(c.expires_on)}` : ""}${
                c.reference ? `<span class="dot">&middot;</span>${esc(c.reference)}` : ""}</div>
            </div>
            ${canEdit ? `<div class="row-side">
              <button class="btn ghost sm" data-drop="${esc(c.id)}">Remove</button></div>` : ""}
          </div>`;
        }).join("") : `<div class="empty">Nothing recorded yet.</div>`}
        ${canEdit ? `<div class="pad"><button class="btn" id="me-cert">Add a certification</button></div>` : ""}
      </div>

      <!-- WHAT THEY COULD TAKE NEXT. The catalog is the same thirteen
           the whole shop is measured on, and the link goes to the study
           material rather than to a sales page. -->
      <div class="card">
        <div class="card-head">Tracks not taken yet</div>
        <div class="ctracks">
          ${TRACKS.filter((t) => !certs.some((c) => trackFor(c.body, c.name)?.code === t.code))
            .map((t) => `
              <div class="ctrack">
                <div class="ctrack-t"><span class="ctrack-code">${esc(t.code)}</span>${esc(t.title)}</div>
                <div class="ctrack-w">${esc(t.what)}</div>
                <a class="ctrack-go" href="${esc(studyUrl(t))}" target="_blank"
                   rel="noopener">Study it &rarr;</a>
              </div>`).join("")}
        </div>
        <div class="pad"><p class="quiet">Sixty questions each, taken on
          <a href="${esc(examUrl())}" target="_blank" rel="noopener">MechanicRank</a>.
          Record the pass here as ${esc(CERT_BODY)} and the three-letter code.</p></div>
      </div>`;

    document.getElementById("me-edit")?.addEventListener("click", () =>
      editSheet(role, id, prof.account_id, sp, prof.phone, who));
    document.getElementById("me-cert")?.addEventListener("click", () =>
      certSheet(role, id, prof.account_id, who));
    document.getElementById("me-pw")?.addEventListener("click", () => passwordSheet());
    document.getElementById("me-motion")?.addEventListener("change", (e) =>
      setMotion((e.currentTarget as HTMLInputElement).checked));

    host.querySelectorAll<HTMLButtonElement>("[data-drop]").forEach((b) =>
      b.addEventListener("click", async () => {
        if (b.dataset["sure"] !== "1") { b.dataset["sure"] = "1"; b.textContent = "Sure?"; return; }
        const sb2 = await db();
        await sb2.from("certifications").delete().eq("id", b.dataset["drop"]);
        void mePage(role, who);
      }));
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

function sheet(title: string, body: string, saveLabel: string): void {
  document.getElementById("mesheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="mesheet">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head"><div><h2>${esc(title)}</h2></div>
          <button class="sheet-x" id="me-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">${body}</div>
        <div class="sheet-acts">
          <button class="btn primary" id="me-save">${esc(saveLabel)}</button>
          <button class="btn ghost" id="me-cancel">Cancel</button></div>
        <div class="msg-slot" id="me-msg"></div>
      </div>
    </div>`);
  const shut = () => document.getElementById("mesheet")?.remove();
  document.getElementById("me-x")?.addEventListener("click", shut);
  document.getElementById("me-cancel")?.addEventListener("click", shut);
}

/** Save with the button locked, and the error said out loud if it fails. */
function onSave(run: () => Promise<void>, label: string): void {
  document.getElementById("me-save")?.addEventListener("click", async () => {
    const b = document.getElementById("me-save") as HTMLButtonElement;
    const msg = document.getElementById("me-msg");
    b.disabled = true; b.textContent = "Saving…";
    try { await run(); } catch (e) {
      b.disabled = false; b.textContent = label;
      if (msg) {
        msg.innerHTML = `<p class="msg bad">${
          esc(e instanceof Error ? e.message : String(e))}</p>`;
      }
    }
  });
}

/**
 * SET A PASSWORD WITHOUT SIGNING OUT.
 *
 * Somebody who came in through a magic link has a live session but no
 * password they know. Signing out to find Forgot Password is the long way
 * round, so this writes a new one straight onto the signed-in user. On
 * success the sheet says so rather than vanishing — a password change with
 * no acknowledgement reads as if nothing happened.
 */
function passwordSheet(): void {
  sheet("Set a password", `
    <label class="field"><span>New password</span>
      <input id="me-newpw" type="password" autocomplete="new-password"></label>
    <p class="quiet small">Use this to sign in next time instead of a link.</p>`,
    "Save password");

  onSave(async () => {
    const pw = val("me-newpw");
    if (pw.length < 6) throw new Error("The password needs at least six characters.");
    await setMyPassword(pw);

    const body = document.querySelector("#mesheet .sheet-body");
    if (body) {
      body.innerHTML = `<p class="msg good">Password set. Next time you can sign in with
        your email and this password instead of a link.</p>`;
    }
    const b = document.getElementById("me-save") as HTMLButtonElement | null;
    if (b) {
      b.disabled = false; b.textContent = "Done";
      b.onclick = () => document.getElementById("mesheet")?.remove();
    }
  }, "Save password");
}

function editSheet(
  role: Role, id: string, accountId: string, sp: Sp | null,
  phone: string | null, who?: string,
): void {
  sheet("Your details", `
    <label class="field"><span>Headline</span>
      <input id="me-headline"
             value="${esc(sp?.headline ?? "")}"></label>
    <label class="field"><span>Years in the trade</span>
      <input id="me-years" type="number" min="0" max="70" value="${esc(sp?.years ?? "")}"></label>
    <label class="field"><span>Mobile</span>
      <input id="me-phone" type="tel" value="${esc(phone ?? "")}"></label>
    <label class="field"><span>Where you have worked</span>
      <textarea id="me-worked" rows="3">${
          esc(sp?.worked_at ?? "")}</textarea></label>
    <label class="field"><span>What to send you</span>
      <textarea id="me-strengths" rows="3">${esc(sp?.strengths ?? "")}</textarea></label>
    <label class="field"><span>What not to send you</span>
      <textarea id="me-avoid" rows="2">${esc(sp?.avoid ?? "")}</textarea></label>
    <p class="quiet small">Being honest here is worth more than looking good.
       An advisor who sends you the wrong job costs you the afternoon.</p>
    <label class="field"><span>Anything else</span>
      <textarea id="me-bio" rows="3">${esc(sp?.bio ?? "")}</textarea></label>`,
    "Save");

  onSave(async () => {
    const sb = await db();
    const yrs = val("me-years");
    const { error } = await sb.from("staff_profiles").upsert({
      profile_id: id, account_id: accountId,
      headline: val("me-headline") || null,
      years: yrs ? Number(yrs) : null,
      worked_at: val("me-worked") || null,
      strengths: val("me-strengths") || null,
      avoid: val("me-avoid") || null,
      bio: val("me-bio") || null,
    }, { onConflict: "profile_id" });
    if (error) throw new Error(error.message);

    // The phone lives on the profile, not here — it is how the shop rings
    // you, and the contact list reads it from there.
    const ph = val("me-phone");
    if (ph !== (phone ?? "")) {
      await sb.from("profiles").update({ phone: ph || null }).eq("id", id);
    }

    document.getElementById("mesheet")?.remove();
    void mePage(role, who);
  }, "Save");
}

function certSheet(role: Role, id: string, accountId: string, who?: string): void {
  sheet("Add a certification", `
    <label class="field"><span>Awarded by</span>
      <input id="mc-body" value="${esc(CERT_BODY)}" list="mc-bodies">
      <datalist id="mc-bodies"><option value="${esc(CERT_BODY)}"><option value="ASE">
        <option value="EPA"></datalist></label>
    <label class="field"><span>What it is</span>
      <input id="mc-name" list="mc-names">
      <datalist id="mc-names">${TRACKS.map((t) =>
        `<option value="${esc(t.code)}">${esc(t.title)}`).join("")}</datalist></label>
    <label class="field"><span>Certificate number</span>
      <input id="mc-ref"></label>
    <label class="field"><span>Earned</span><input id="mc-earned" type="date"></label>
    <label class="field"><span>Expires</span><input id="mc-expires" type="date"></label>
    <p class="quiet small">Leave the expiry blank only if it genuinely never lapses.
       A blank date will never warn anybody.</p>`, "Add it");

  onSave(async () => {
    if (!val("mc-body") || !val("mc-name")) {
      throw new Error("It needs an awarding body and a name.");
    }
    // A three-letter entry is a track code, and the codes are upper case.
    const raw = val("mc-name");
    const name = raw.length === 3 ? raw.toUpperCase() : raw;

    const sb = await db();
    const { error } = await sb.from("certifications").insert({
      account_id: accountId, profile_id: id, technician_id: null,
      body: val("mc-body"), name,
      reference: val("mc-ref") || null,
      earned_on: val("mc-earned") || null,
      expires_on: val("mc-expires") || null,
    });
    if (error) throw new Error(error.message);
    document.getElementById("mesheet")?.remove();
    void mePage(role, who);
  }, "Add it");
}
